import React, {useCallback, useEffect, useState} from 'react';
import axios from 'axios';
import {useNavigate} from 'react-router-dom';
import {ArrowLeft, Clock, Warning} from '@phosphor-icons/react';
import {SigninForm} from '../molecules/SigninForm';
import {NEUGENCE_API_BASE, bearerHeaders, PERIOD_OPTIONS} from './_lib';

const DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

export default function TimingHeatmap() {
	const navigate = useNavigate();
	const [period, setPeriod] = useState('7d');
	const [view, setView] = useState('click'); // 'open' | 'click'
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const signedIn = typeof window !== 'undefined' && !!sessionStorage.getItem('signedin');

	const load = useCallback(async () => {
		setLoading(true); setError(null);
		try {
			const headers = await bearerHeaders();
			const res = await axios.get(`${NEUGENCE_API_BASE}/api/v1/timing-heatmap?period=${period}`, {headers});
			setData(res.data || {});
		} catch (e) { setError(e.message); }
		finally { setLoading(false); }
	}, [period]);

	useEffect(() => { if (signedIn) load(); }, [load, signedIn]);
	if (!signedIn) return <div style={{marginTop: 300}}><SigninForm/></div>;

	const grid = (data && data[view==='click'?'click_grid':'open_grid']) || Array.from({length:7}, () => Array(24).fill(0));
	const max = Math.max(1, ...grid.flat());
	const cellColor = (n) => {
		if (n === 0) return '#f1f5f9';
		const intensity = Math.min(1, n/max);
		const c = view==='click' ? `rgba(239, 68, 68, ${0.15+intensity*0.85})` : `rgba(245, 158, 11, ${0.15+intensity*0.85})`;
		return c;
	};

	return (
		<div className="min-h-screen bg-white text-left">
			<div className="max-w-6xl mx-auto px-6 py-10">
				<button onClick={() => navigate('/dashboard')} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-6">
					<ArrowLeft size={14}/> Dashboard
				</button>
				<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
					<div>
						<h1 className="text-3xl font-semibold tracking-tight text-slate-900 flex items-center gap-3">
							<Clock size={28} weight="duotone" color="#f59e0b"/> Timing heatmap
						</h1>
						<p className="text-sm text-slate-500 mt-1">When opens and clicks happen (UTC). Use this to schedule the next send.</p>
					</div>
					<div className="flex gap-2">
						<div role="tablist" className="inline-flex rounded-full bg-slate-100 p-1">
							{PERIOD_OPTIONS.map(p => (
								<button key={p.key} onClick={() => setPeriod(p.key)}
									className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${period===p.key?'bg-white text-slate-900 shadow-sm':'text-slate-600 hover:text-slate-900'}`}>{p.label}</button>
							))}
						</div>
						<div role="tablist" className="inline-flex rounded-full bg-slate-100 p-1">
							<button onClick={() => setView('click')} className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${view==='click'?'bg-white text-slate-900 shadow-sm':'text-slate-600 hover:text-slate-900'}`}>Clicks</button>
							<button onClick={() => setView('open')} className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${view==='open'?'bg-white text-slate-900 shadow-sm':'text-slate-600 hover:text-slate-900'}`}>Opens</button>
						</div>
					</div>
				</div>

				{error && <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 mb-6"><Warning size={18} className="text-amber-600 inline mr-2"/>{String(error)}</div>}
				{loading && <div className="text-center text-sm text-slate-500 py-20">Loading…</div>}

				{!loading && (
					<div className="rounded-2xl border border-slate-200 bg-white overflow-hidden p-6">
						<div className="overflow-x-auto">
							<table className="text-xs">
								<thead>
									<tr>
										<th className="px-2 py-1 text-slate-500"></th>
										{Array.from({length:24}, (_,h) => <th key={h} className="px-1 py-1 text-slate-400 font-normal">{h}</th>)}
									</tr>
								</thead>
								<tbody>
									{DAYS.map((day, dow) => (
										<tr key={day}>
											<td className="px-2 py-0.5 text-slate-500 font-medium">{day}</td>
											{Array.from({length:24}, (_,h) => {
												const n = grid[dow][h];
												return <td key={h} title={`${day} ${h}:00 UTC — ${n} ${view}s`} style={{backgroundColor: cellColor(n), width:24, height:20}} className="border border-white"></td>;
											})}
										</tr>
									))}
								</tbody>
							</table>
						</div>
						<div className="text-xs text-slate-500 mt-4">
							Total {view}s: <strong>{(data && data.totals && data.totals[view+'s']) || 0}</strong>. Cells shaded by event count; UTC hour buckets.
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
